import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
  this.dispatchEvent(new MegaMenuHoverEvent());

  if (!(event.target instanceof Element)) return;

  let item = findMenuItem(event.target);

  if (!item || item == this.#state.activeItem) return;

  const isDefaultSlot = event.target.slot === '';

  this.dataset.overflowExpanded = (!isDefaultSlot).toString();

  const previouslyActiveItem = this.#state.activeItem;

  if (previouslyActiveItem) {
    previouslyActiveItem.ariaExpanded = 'false';
  }

  this.#state.activeItem = item;
  this.ariaExpanded = 'true';
  item.ariaExpanded = 'true';

  let submenu = findSubmenu(item);

  if (!submenu && !isDefaultSlot) {
    submenu = this.overflowMenu;
  }

  if (submenu) {
    
    const parentLi = item.closest('li');
    parentLi?.classList.add('is-active');

    // ---- EXISTING CODE (UNCHANGED) ----

    submenu.dataset.active = '';

    this.#cleanupMutationObserver();

    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (submenu.offsetHeight > 0) {
            this.headerComponent?.style.setProperty(
              '--submenu-height',
              `${submenu.offsetHeight}px`
            );
            this.#cleanupMutationObserver();
          }
        });
      });
    });

    this.#submenuMutationObserver.observe(submenu, {
      childList: true,
      subtree: true
    });

    setTimeout(() => {
      this.#cleanupMutationObserver();
    }, 500);
  }

  const submenuHeight = submenu ? submenu.offsetHeight : 0;

  this.headerComponent?.style.setProperty(
    '--submenu-height',
    `${submenuHeight}px`
  );
  this.style.setProperty('--submenu-opacity', '1');
};


  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
 deactivate(event) {
  if (!(event.target instanceof Element)) return;

  const menu = findSubmenu(this.#state.activeItem);
  const isMovingWithinMenu =
    event.relatedTarget instanceof Node &&
    menu?.contains(document.activeElement);

  const isMovingToSubmenu =
    event.relatedTarget instanceof Node &&
    event.type === 'blur' &&
    menu?.contains(event.relatedTarget);

  const isMovingToOverflowMenu =
    event.relatedTarget instanceof Node &&
    event.relatedTarget.parentElement?.matches('[slot="overflow"]');

  if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

  if (this.#state.activeItem) {
    const parentLi = this.#state.activeItem.closest('li');
    parentLi?.classList.remove('is-active');
  }

  this.#deactivate();
}


  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
